import { Injectable } from '@angular/core';
import { uniqBy } from 'lodash';
import { CommonRegEx, LmapService, TWAMP_MODE } from '@nrwl/common-lib';
import { IPAddressValue, TwampConfigFieldMapping } from '@nrwl/twamp-test';
import { vlanConfigValue } from '@nrwl/y1564-test';

@Injectable()
export class TwampTestSetupConfigService {
  constructor(private readonly lmap: LmapService) {}
  testConfig: any;

  //eslint-disable-next-line
  getTwampOutputParams(testConfig: any): any {
    this.testConfig = testConfig;
    const config = {};
    const primaryAgentInfo = this.lmap.getAgentInfo('primary', this.testConfig.config);
    if (primaryAgentInfo) {
      const primaryConfig = this.lmap.getMATestConfigByName(
        this.testConfig.config,
        primaryAgentInfo.config
      );
      if (primaryConfig) {
        //PM Interval
        const pmResultSchedule = this.lmap.extractReportScheduleByType(primaryConfig, 'pm');
        if (pmResultSchedule && pmResultSchedule.start) {
          const pmIntObj = this.lmap.extractEventByName(primaryConfig, pmResultSchedule.start);
          if (pmIntObj && pmIntObj.periodic && typeof pmIntObj.periodic.interval === 'number') {
            config[TwampConfigFieldMapping.PM_INTERVAL] = parseInt(pmIntObj.periodic.interval);
          }
        }

        const testSchedule = this.lmap.extractScheduleByTaskName(primaryConfig, 'twamp-initiator');
        if (testSchedule) {
          //Subnet
          const prefixLength = this.lmap.extractScheduleOptionByName(testSchedule, 'prefix-length');
          if (prefixLength) {
            config[TwampConfigFieldMapping.SUBNET] = prefixLength;
          }
          const subnet = this.lmap.extractScheduleOptionByName(testSchedule, 'subnet');
          if (subnet) {
            config[TwampConfigFieldMapping.SUBNET] = subnet;
          }
          //Gateway
          const gateway = this.lmap.extractScheduleOptionByName(testSchedule, 'gateway');
          if (gateway) {
            config[TwampConfigFieldMapping.GATEWAY] = gateway;
          }

          // process the keep alive option
          //Keep Alive
          const keepAliveOption = this.lmap.extractScheduleOptionByName(
            testSchedule,
            'twamp-keep-alive-interval'
          );

          config[TwampConfigFieldMapping.KEEP_ALIVE] =
            keepAliveOption && keepAliveOption !== '0' ? true : false;

          //Reboot
          let restartOnBootOption = this.lmap.extractScheduleOptionByName(
            testSchedule,
            'twamp-restart-on-boot'
          );
          config[TwampConfigFieldMapping.REBOOT] = restartOnBootOption ? true : false;
          restartOnBootOption = this.lmap.extractScheduleOptionByName(testSchedule, 'persist');
          config[TwampConfigFieldMapping.REBOOT] = restartOnBootOption ? true : false;

          //Enable Percentiles
          const percentilesVal = this.lmap.extractScheduleOptionByName(
            testSchedule,
            'enable-percentiles'
          );
          config[TwampConfigFieldMapping.ENABLE_PERCENTILES] =
            percentilesVal && percentilesVal === 'true' ? true : false;

          //Codec
          const codecVal = this.lmap.extractScheduleOptionByName(testSchedule, 'codec');
          if (codecVal) {
            config[TwampConfigFieldMapping.CODEC] = codecVal;
          }

          const engineType = this.lmap.extractScheduleOptionByName(testSchedule, 'engine-type');
          if (engineType) {
            config[TwampConfigFieldMapping.AGENT_TYPE] = engineType;
            const txInterface = this.lmap.extractScheduleOptionByName(testSchedule, 'tx-interface');
            if (txInterface) {
              config[TwampConfigFieldMapping.TRANSMIT_INTERFACE] = txInterface;
              config[TwampConfigFieldMapping.DEFINED_ADDRESS] = 'testIP';
            }
          }

          //Vlans
          const vlanId = this.lmap.extractScheduleOptionByName(testSchedule, 'vlan-id');
          const cVlanId = this.lmap.extractScheduleOptionByName(testSchedule, 'inner-vlan-id');
          if (vlanId) {
            config[TwampConfigFieldMapping.SVLAN_ID] = vlanId;
            config[TwampConfigFieldMapping.SVLAN_PRIORITY] =
              this.lmap.extractScheduleOptionByName(testSchedule, 'vlan-priority') || '0';
            if (cVlanId) {
              config[TwampConfigFieldMapping.CVLAN_ID] = cVlanId;
              config[TwampConfigFieldMapping.CVLAN_PRIORITY] =
                this.lmap.extractScheduleOptionByName(testSchedule, 'inner-vlan-priority') || '0';
              config[TwampConfigFieldMapping.CVLAN_ETHER_TYPE] =
                this.lmap.extractScheduleOptionByName(testSchedule, 'tpid');
            }
          }

          config[TwampConfigFieldMapping.VLAN_CONFIG] =
            vlanId && cVlanId
              ? vlanConfigValue.Q_IN_Q
              : vlanId
              ? vlanConfigValue.SINGLE_VLAN
              : vlanConfigValue.UNTAGGED;

          let testOptions = this.lmap.extractScheduleOptionByName(testSchedule, 'twamp');
          let fullConnection = [];
          if (testOptions) {
            const map = this.lmap.extractScheduleOptionByName(testSchedule, 'test-session-map');
            fullConnection = this.processControllerFullConfig(testOptions, map);
          }
          let lightConnection = [];
          testOptions = this.lmap.extractScheduleOptionByName(testSchedule, 'twamp-light');
          if (testOptions) {
            const map = this.lmap.extractScheduleOptionByName(testSchedule, 'session-map');
            const groupmap = this.lmap.extractScheduleOptionByName(
              testSchedule,
              'session-group-map'
            );
            lightConnection = this.processControllerLightConfig(testOptions, map, groupmap);
          }
          const combinedConnection = [...fullConnection, ...lightConnection];
          let selectedTab =
            this.testConfig?.config?.test?.['extended-config']?.[
              TwampConfigFieldMapping.SELECTED_TAB
            ];
          selectedTab = selectedTab >= 0 ? selectedTab : 1;
          config[TwampConfigFieldMapping.SELECTED_TAB] = selectedTab;
          const specificFlows = this.getSpecificIpflows(combinedConnection);
          if (selectedTab === 1) {
            config[TwampConfigFieldMapping.SPECIFIC_FLOWS] = specificFlows;
          } else {
            config[TwampConfigFieldMapping.COMMON_FLOWS] = combinedConnection?.[0].flows;
          }

          //other fields
          config[TwampConfigFieldMapping.SELECTED_REFLECTOR_IDS] = this.getReflectorsId();
          config[TwampConfigFieldMapping.REFLECTOR_UDP] =
            this.testConfig?.config?.test?.['extended-config']?.[
              TwampConfigFieldMapping.REFLECTOR_UDP
            ];

          const destinationDetails = this.getDestinationReflectorDetails(combinedConnection);
          config[TwampConfigFieldMapping.ADDRESS] = combinedConnection?.[0]?.intiatorIp;
          config[TwampConfigFieldMapping.IP_ADDRESS_TYPE] = this.getIpAddressType(
            combinedConnection?.[0]?.intiatorIp
          );
          config[TwampConfigFieldMapping.BASE_UDP] = combinedConnection?.[0]?.baseUdp;
          config[TwampConfigFieldMapping.SELECTED_DESTINATION] =
            this.frameReflectorObject(destinationDetails);
        }
      }
    }

    return config;
  }

  getIpAddressType(ipAddress: string): string {
    if (CommonRegEx.IPV6.test(ipAddress)) {
      return IPAddressValue.IP_V6;
    } else {
      return IPAddressValue.IP_V4;
    }
  }

  //eslint-disable-next-line
  getSpecificIpflows(connectionData: any): any {
    const flowsInfo = {};
    connectionData.forEach(data => {
      flowsInfo[data.ipAddress] = data.flows;
    });
    return flowsInfo;
  }

  //eslint-disable-next-line
  processControllerFullConfig(testOptions: any, map: any): any {
    const overallConnectionInfo = [];
    const flowsFields = {
      name: '',
      dscp: '',
      frameSize: null,
      txInterval: null,
      padding: null,
      paddingMin: 0,
      paddingMax: 8913,
      frameSizeMin: 87,
      frameSizeMax: 9000,
      dfBit: false,
      reflectorUDP: 50000
    };
    if (map) {
      map = JSON.parse(map);
    }
    testOptions = JSON.parse(testOptions);

    if (
      testOptions['ietf-twamp:twamp'] &&
      testOptions['ietf-twamp:twamp']['twamp-client'] &&
      testOptions['ietf-twamp:twamp']['twamp-client']['twamp-client-ctrl-connection'] &&
      testOptions['ietf-twamp:twamp']['twamp-client']['twamp-client-ctrl-connection'].length
    ) {
      const probeConnArray =
        testOptions['ietf-twamp:twamp']['twamp-client']['twamp-client-ctrl-connection'];
      const numConns = probeConnArray.length;

      let senderTestSessionArray = null;
      if (
        testOptions['ietf-twamp:twamp']['twamp-client']['twamp-session-sender'] &&
        testOptions['ietf-twamp:twamp']['twamp-client']['twamp-session-sender'][
          'twamp-sender-test-session'
        ] &&
        testOptions['ietf-twamp:twamp']['twamp-client']['twamp-session-sender'][
          'twamp-sender-test-session'
        ].length > 0
      ) {
        senderTestSessionArray =
          testOptions['ietf-twamp:twamp']['twamp-client']['twamp-session-sender'][
            'twamp-sender-test-session'
          ];
      }

      for (let y = 0; y < numConns; ++y) {
        const connectionInfo = {
          intiatorIp: null,
          ipAddress: null,
          baseUdp: null,
          port: null,
          type: TWAMP_MODE.FULL,
          flows: []
        };
        if (
          probeConnArray[y]['twamp-session-request'] &&
          probeConnArray[y]['twamp-session-request'].length &&
          probeConnArray[y]['twamp-session-request'].length > 0
        ) {
          const numFlows = probeConnArray[y]['twamp-session-request'].length;
          for (let flows = 0; flows < numFlows; flows++) {
            const flowsItem = JSON.parse(JSON.stringify(flowsFields));
            connectionInfo.flows.push(flowsItem);
          }
          for (const z in probeConnArray[y]) {
            switch (z) {
              case 'client-ip':
                connectionInfo['intiatorIp'] = probeConnArray[y][z];
                break;
              case 'server-ip':
                connectionInfo['ipAddress'] = probeConnArray[y][z];
                break;
              case 'server-tcp-port':
                connectionInfo['port'] = probeConnArray[y][z];
                break;
              case 'twamp-session-request':
                for (let flowIdx = 0; flowIdx < numFlows; ++flowIdx) {
                  for (const flowAttr in probeConnArray[y][z][flowIdx]) {
                    switch (flowAttr) {
                      case 'test-session-name': {
                        const flowName = probeConnArray[y][z][flowIdx][flowAttr];
                        let dfBit = false;
                        if (map) {
                          if (map[flowName]) {
                            dfBit = map[flowName]['df-bit'] ? map[flowName]['df-bit'] : false;
                          }
                        }
                        connectionInfo['flows'][flowIdx].name = flowName;
                        connectionInfo['flows'][flowIdx].dfBit = dfBit;
                        // use the flow name to look up the tx interval
                        for (const senderIdx in senderTestSessionArray) {
                          if (
                            senderTestSessionArray[senderIdx]['test-session-name'] ===
                              connectionInfo.flows[flowIdx].name &&
                            senderTestSessionArray[senderIdx]['periodic-interval']
                          ) {
                            connectionInfo.flows[flowIdx].txInterval =
                              senderTestSessionArray[senderIdx]['periodic-interval'];
                            break;
                          }
                        }
                        break;
                      }
                      case 'sender-udp-port':
                        // we require it to be the same for all flows (starting src port)
                        connectionInfo.baseUdp = probeConnArray[y][z][flowIdx][flowAttr];
                        break;
                      case 'reflector-udp-port':
                        connectionInfo.flows[flowIdx].reflectorUDP =
                          probeConnArray[y][z][flowIdx][flowAttr];
                        break;
                      // case 'timeout':
                      //     newConnection.flows[flowIdx].flowReflWaitTimeout = probeConnArray[y][z][flowIdx][flowAttr];
                      //     break;
                      case 'padding-length': {
                        connectionInfo.flows[flowIdx].padding =
                          probeConnArray[y][z][flowIdx][flowAttr];
                        break;
                      }
                      case 'dscp':
                        connectionInfo.flows[flowIdx].dscp =
                          probeConnArray[y][z][flowIdx][flowAttr];
                        break;
                      default:
                        console.log('Connection configuration setting not handled: ' + z);
                        break;
                    }
                  }
                }
                break;
              default:
                console.log('Connection configuration setting not handled: ' + z);
                break;
            }
          }
          overallConnectionInfo.push(connectionInfo);
        }
      }
    }
    return overallConnectionInfo;
  }

  //eslint-disable-next-line
  processControllerLightConfig(testOptions: any, map: any, groupmap: any): any {
    const overallConnectionInfo = [];

    const flowsFields = {
      name: '',
      dscp: '',
      frameSize: null,
      txInterval: null,
      padding: null,
      paddingMin: 0,
      paddingMax: 8913,
      frameSizeMin: 87,
      frameSizeMax: 9000,
      dfBit: false,
      reflectorUDP: 50000
    };
    //let sessionConfig = 'twampLight';
    // Note: Map is optional...our UI produces it but other clients may not
    if (map) {
      map = JSON.parse(map);
    }

    if (groupmap) {
      groupmap = JSON.parse(groupmap);
    }

    testOptions = JSON.parse(testOptions);

    if (
      testOptions['ietf-twamp-light:twamp-light'] &&
      testOptions['ietf-twamp-light:twamp-light']['twamp-light-session-sender'] &&
      testOptions['ietf-twamp-light:twamp-light']['twamp-light-session-sender']['test-session'] &&
      testOptions['ietf-twamp-light:twamp-light']['twamp-light-session-sender']['test-session']
        .length
    ) {
      const probeFlowArray =
        testOptions['ietf-twamp-light:twamp-light']['twamp-light-session-sender']['test-session'];
      const totalFlows = probeFlowArray.length;

      const processedDestinations = [];

      for (let flowNum = 0; flowNum < totalFlows; ++flowNum) {
        if (probeFlowArray[flowNum]['reflector-ip']) {
          const thisConnDstIp = probeFlowArray[flowNum]['reflector-ip'];
          let alreadyProcessed = false;

          // There is no notion of connection with twamp-light
          // so we put all flows with same dest IP into one connection
          for (const z in processedDestinations) {
            if (processedDestinations[z] === thisConnDstIp) {
              alreadyProcessed = true;
              break;
            }
          }

          if (alreadyProcessed === true) {
            continue;
          }

          const connectionInfo = {
            intiatorIp: null,
            ipAddress: null,
            baseUdp: null,
            port: null,
            type: TWAMP_MODE.LIGHT,
            flows: []
          };

          // count the number of flows this dest IP
          processedDestinations.push(thisConnDstIp);

          const probeFlowArrayFlows = probeFlowArray.filter(
            item => item['reflector-ip'] === thisConnDstIp
          );
          probeFlowArrayFlows.forEach(item => {
            const flowsItem = JSON.parse(JSON.stringify(flowsFields));
            connectionInfo.flows.push(flowsItem);
          });

          // process all flows with this dest IP
          let flowIdx = 0;
          // let sessionName = '';
          for (const z in probeFlowArray) {
            if (thisConnDstIp === probeFlowArray[z]['reflector-ip']) {
              for (const y in probeFlowArray[z]) {
                // Default session names
                //	sessionName = 'dest ' + probeFlowArray[z]['reflector-ip'];
                switch (y) {
                  case 'session-id': {
                    let name = probeFlowArray[z][y];
                    let dfBit = false;
                    // look name up in map if provided
                    if (map) {
                      for (const entry in map) {
                        if (entry === name.toString() && map[entry].name) {
                          name = map[entry].name;
                          dfBit = map[entry]['df-bit'] ? map[entry]['df-bit'] : false;
                          connectionInfo.type =
                            map[entry]['payload-type'] === 1 ? TWAMP_MODE.PING : TWAMP_MODE.LIGHT;
                          break;
                        }
                      }
                    }

                    connectionInfo.flows[flowIdx].name = name;
                    connectionInfo.flows[flowIdx].dfBit = dfBit;
                    break;
                  }
                  case 'sender-ip':
                    // we require it to be the same for all flows in a test
                    connectionInfo.intiatorIp = probeFlowArray[z][y];
                    break;
                  case 'sender-udp-port':
                    // we require it to be the same for all flows (starting src port)
                    connectionInfo.baseUdp = probeFlowArray[z][y];
                    break;
                  case 'reflector-ip':
                    connectionInfo.ipAddress = probeFlowArray[z][y];
                    break;
                  case 'reflector-udp-port':
                    connectionInfo.flows[flowIdx].reflectorUDP = probeFlowArray[z][y];
                    break;
                  case 'interval':
                    connectionInfo.flows[flowIdx].txInterval = probeFlowArray[z][y];
                    break;
                  case 'packet-padding-size':
                    connectionInfo.flows[flowIdx].padding = probeFlowArray[z][y];
                    break;
                  case 'dscp':
                    connectionInfo.flows[flowIdx].dscp = probeFlowArray[z][y];
                    break;
                  case 'session-authentication-mode':
                  case 'test-session-enable':
                  case 'payload-type':
                    // known fields that we skip because there are not UI settings for these items
                    break;
                  default:
                    console.log('Flow configuration setting not handled: ' + y);
                    break;
                }
              }
              flowIdx++;
            }
          }
          overallConnectionInfo.push(connectionInfo);
        }
      }
    }
    return overallConnectionInfo;
  }

  //use for older fusion as it dont have any ids reference
  //eslint-disable-next-line
  getDestinationReflectorDetails(combinedConnection: any): any {
    combinedConnection.forEach(item => {
      if (item?.flows?.[0]?.name) {
        const fullName = item.flows[0].name.split('_');
        item.name = fullName[fullName.length - 2];
      }
    });
    const twampReflectors = combinedConnection.map(({ ipAddress, port, type, name }) => ({
      ipAddress,
      port,
      type,
      name
    }));
    return uniqBy(twampReflectors, 'ipAddress');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getReflectorsId(): any {
    const testConfigData = this.testConfig.config;
    if (!testConfigData?.test) return [];
    const deviceIds =
      testConfigData.test['extended-config'] &&
      testConfigData.test['extended-config']['testPoints'].filter(
        item => item.tag === 'Test Point B' && item?.id
      );
    return deviceIds;
  }

  // eslint-disable-next-line
  frameReflectorObject(reflectors: any): any {
    const response = [];
    reflectors.forEach(item => {
      const configIn = {};
      configIn['id'] = item.ipAddress;
      configIn['key'] = item.ipAddress;
      configIn['name'] = { label: item.name ? item.name : item.ipAddress };
      configIn['itemType'] = 'device';
      configIn['subLabel'] = item.type;
      configIn['ipAddress'] = { label: item.ipAddress };
      configIn['reflectorType'] = { label: item.type };
      configIn['dataInfo'] = { port: item.port, twampMode: item.type };
      configIn['deviceData'] = {
        ipaddress: item.ipAddress,
        capabilities: {
          [item.type]: {
            'udp-port': item.port
          }
        }
      };
      response.push(configIn);
    });
    return response;
  }
}
